<template>
	<v-dialog max-width="2000" v-model="dialog" :fullscreen="$vuetify.breakpoint.xsOnly" persistent>
		<v-card>
			<v-card-title class="card-header-add font-weight-bold pa-4">
				<span class="font-size15">
					{{ $t('Reports Dialog') }}:
					{{ formatBillingDate(getCsvDataPreview.patientsListRPM[0].month, getCsvDataPreview.patientsListRPM[0].year) }}
					-
					{{ getCsvDataPreview.patientsListRPM[0].orgName }}
				</span>
				<v-spacer />
				<v-radio-group v-model="selectedReportType" row class="mr-4 my-0 pt-0" hide-details>
					<v-radio label="RPM" value="RPM" color="white" class="mr-2" dark hide-details dense outlined />
					<v-radio label="RTM" value="RTM" color="white" class="mr-2" dark hide-details dense outlined />
					<v-radio label="CCM" value="CCM" color="white" class="mr-2" dark hide-details dense outlined />
				</v-radio-group>
				<v-icon color="white" @click="closeDialog">mdi-window-close</v-icon>
			</v-card-title>
			<v-card-text class="pa-4">
				<v-data-table
					:items="reports"
					:headers="reportsHeaders"
					:no-data-text="$t('no-data-available')"
					:loading-text="$t('loading')"
					:headers-length="reportsHeaders.length"
					:footer-props="getDataTableFooterProps('reportsDialogTable')"
					:items-per-page="-1"
					fixed-header
					dense
					:height="pageHeight(57)"
					class="medical-staff-data-table elevation-2">
					<template #item="{ item }" v-if="selectedReportType === 'RTM' || selectedReportType === 'RPM'">
						<tr>
							<td class="font-size12 px-1 text-left">
								{{ item.officeName }}
							</td>
							<td class="font-size12 px-1 text-left">{{ item.patientFirstName }} {{ item.patientLastName }}</td>
							<td class="font-size12 px-1 text-left">
								{{ formatDate(item.birthdate) }}
							</td>
							<td class="font-size12 px-1 text-left">
								{{ getGender(item.gender) }}
							</td>
							<td class="font-size12 px-1 text-left">
								{{ item.orderingProviderName }}
							</td>
							<td class="font-size12 px-1 text-left">
								<div class="overflow-y-auto">
									{{ item.problemList.replace(/\s\|\s+/g, ' ') }}
								</div>
							</td>
							<td class="font-size12 px-1 text-left">
								{{ item.cpt99453_DeviceSetupFee }}
							</td>
							<td class="font-size12 px-1 text-left">
								{{ formatDate(item.cpt99453_16thReadingDate) }}
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20" v-if="item.cpt99453_Billable === 'Yes'">mdi-checkbox-blank</v-icon>
								<v-icon size="20" v-else>mdi-checkbox-blank-outline</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								{{ item.cpt99454_TotalMeasurements }}
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20" v-if="item.cpt99454_Billable === 'Yes'">mdi-checkbox-blank</v-icon>
								<v-icon size="20" v-else>mdi-checkbox-blank-outline</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20" v-if="item.cpt99457_Billable === 'Yes'">mdi-checkbox-blank</v-icon>
								<v-icon size="20" v-else>mdi-checkbox-blank-outline</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20" v-if="item.cpt99458_Billable === 'Yes'">mdi-checkbox-blank</v-icon>
								<v-icon size="20" v-else-if="item.cpt99458_Billable === 'No'"> mdi-checkbox-blank-outline </v-icon>
								<template v-else-if="item.cpt99458_Billable === 'Yes x2'">
									<v-icon size="20">mdi-checkbox-blank</v-icon>
									<v-icon size="20">mdi-checkbox-blank</v-icon>
								</template>
							</td>

							<td class="font-size12 px-1 text-left">
								{{ item.cpt99457_Cpt99458_TotalMinutes }}
							</td>
						</tr>
					</template>
					<template #item="{ item }" v-else-if="selectedReportType === 'CCM'">
						<tr>
							<td class="font-size12 px-1 text-left">
								{{ item.officeName }}
							</td>
							<td class="font-size12 px-1 text-left">{{ item.patientFirstName }} {{ item.patientLastName }}</td>
							<td class="font-size12 px-1 text-left">
								{{ formatDate(item.birthdate) }}
							</td>
							<td class="font-size12 px-1 text-left">
								{{ getGender(item.gender) }}
							</td>
							<td class="font-size12 px-1 text-left">
								{{ item.orderingProviderName }}
							</td>
							<td class="font-size12 px-1 text-left">
								<div class="overflow-y-auto">
									{{ item.problemList.replace(/\s\|\s+/g, ' ') }}
								</div>
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20">
									{{ item.cptCcm99490_Billable === 'Yes' ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
								</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20">
									{{ item.cptCcm99439_Billable === 'Yes' ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
								</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								{{ item.cptCcm99490_CptCcm99439_TotalMinutes }}
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20">
									{{ item.cptCcm99487_Billable === 'Yes' ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
								</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20">
									{{ item.cptCcm99489_Billable === 'Yes' ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
								</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								{{ item.cptCcm99487_CptCcm99489_TotalMinutes }}
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20">
									{{ item.cptCcm99491_Billable === 'Yes' ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
								</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								<v-icon size="20">
									{{ item.cptCcm99437_Billable === 'Yes' ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
								</v-icon>
							</td>
							<td class="font-size12 px-1 text-left">
								{{ item.cptCcm99491_CptCcm99437_TotalMinutes }}
							</td>
						</tr>
					</template>
				</v-data-table>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { getDataTableFooterProps } from '@/utils/utilities';
export default {
	props: ['dialog', 'detailedUser'],
	data () {
		return {
			localDialog: this.dialog,
			selectedReportType: 'RPM',
		};
	},
	computed: {
		...mapState({
			getCsvDataPreview: (state) => state.billings.getCsvDataPreview,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		reportsHeaders () {
			switch (this.selectedReportType) {
				case 'CCM':
					return [
						{ text: this.$t('location-name'), value: 'OfficeName', class: 'color-header font-size11 px-1' },
						{ text: this.$t('patient-name'), value: 'PatientFirstName', class: 'color-header font-size11 px-1' },
						{ text: this.$t('birthdate'), value: 'Birthdate', class: 'color-header px-1' },
						{ text: this.$t('gender'), value: 'Gender', class: 'color-header px-1' },
						{ text: this.$t('Provider Name'), value: 'OrderingProviderName', class: 'color-header px-1' },
						{ text: this.$t('problem-list'), value: 'ProblemList', class: 'color-header px-1' },
						{ text: this.$t('99490'), value: 'cptCcm99490_Billable', class: 'color-header px-1', width: '50' },
						{ text: this.$t('99439'), value: 'cptCcm99439_Billable', class: 'color-header px-1', width: '50' },
						{
							text: this.$t('99490,99439 Minutes'),
							value: 'cptCcm99490_CptCcm99439_TotalMinutes',
							class: 'color-header px-1',
						},
						{ text: this.$t('99487'), value: 'cptCcm99457_Billable', class: 'color-header px-1', width: '50' },
						{ text: this.$t('99489'), value: 'cptCcm99458_Billable', class: 'color-header px-1', width: '50' },
						{
							text: this.$t('99487,99489 Minutes'),
							value: 'cptCcm99487_CptCcm99489_TotalMinutes',
							class: 'color-header px-1',
						},
						{ text: this.$t('99491'), value: 'cptCcm99491_Billable', class: 'color-header px-1', width: '50' },
						{ text: this.$t('99437'), value: 'cptCcm99437_Billable', class: 'color-header px-1', width: '50' },
						{
							text: this.$t('99491,99437 Minutes'),
							value: 'cptCcm99491_CptCcm99437_TotalMinutes',
							class: 'color-header px-1',
						},
					];
				default:
					return [
						{ text: this.$t('location-name'), value: 'OfficeName', class: 'color-header font-size11 px-1' },
						{ text: this.$t('patient-name'), value: 'PatientFirstName', class: 'color-header font-size11 px-1' },
						{ text: this.$t('birthdate'), value: 'Birthdate', class: 'color-header px-1' },
						{ text: this.$t('gender'), value: 'Gender', class: 'color-header px-1' },
						{ text: this.$t('Provider Name'), value: 'OrderingProviderName', class: 'color-header px-1' },
						{ text: this.$t('problem-list'), value: 'ProblemList', class: 'color-header px-1' },
						{ text: this.$t('99453 Setup fee'), value: 'Cpt99453_DeviceSetupFee', class: 'color-header px-1' },
						{
							text: this.$t('99453 16th Reading date'),
							value: 'Cpt99453_16thReadingDate',
							class: 'color-header px-1',
							width: '100',
						},
						{ text: this.$t('99453'), value: 'Cpt99453_Billable', class: 'color-header px-1', width: '50' },
						{
							text: this.$t('99454 Measurements'),
							value: 'Cpt99454_TotalMeasurements',
							class: 'color-header px-1',
							width: '100',
						},
						{ text: this.$t('99454'), value: 'Cpt99454_Billable', class: 'color-header px-1', width: '50' },
						{ text: this.$t('99457'), value: 'Cpt99457_Billable', class: 'color-header px-1', width: '50' },
						{ text: this.$t('99458'), value: 'Cpt99458_Billable', class: 'color-header px-1', width: '50' },
						{
							text: this.$t('99457,99458 Minutes'),
							value: 'Cpt99457_Cpt99458_TotalMinutes',
							class: 'color-header px-1',
						},
					];
			}
		},
		reports () {
			switch (this.selectedReportType) {
				case 'RPM':
					return this.getCsvDataPreview.patientsListRPM;
				case 'RTM':
					return this.getCsvDataPreview.patientsListRTM;
				case 'CCM':
					return this.getCsvDataPreview.patientsListCCM;
				default:
					return [];
			}
		},
	},
	methods: {
		getDataTableFooterProps,
		getGender (gender) {
			switch (gender) {
				case 'F':
					return this.$t('female');
				case 'M':
					return this.$t('male');
			}
		},
		formatDate (dateString) {
			const date = new Date(dateString); // Convert the string to a Date object
			return date.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});
		},
		formatBillingDate (billingMonth, billingYear) {
			// Convert the full month name to a numeric value
			const monthNames = [
				'JANUARY',
				'FEBRUARY',
				'MARCH',
				'APRIL',
				'MAY',
				'JUNE',
				'JULY',
				'AUGUST',
				'SEPTEMBER',
				'OCTOBER',
				'NOVEMBER',
				'DECEMBER',
			];
			const monthIndex = monthNames.indexOf(billingMonth.toUpperCase());
			if (monthIndex === -1) {
				throw new Error('Invalid month name');
			}
			// Use the existing logic to format the date
			const date = new Date(billingYear, monthIndex, 1);
			return date.toLocaleDateString('en-US', {
				month: 'short',
				year: 'numeric',
			});
		},
		closeDialog () {
			this.$emit('close-dialog'); // emit event to update par
		},
	},
};
</script>
